import { IRowData } from "@multiweb/types/contracts";
import { IDataSourceLoadInfo } from "../../../Global/Base/DataSources/Interfaces";
import { CultureInfo } from "../../../Global/Dom/CultureInfo";
import { createElement, registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarGrid, IntervalDirection } from "../CalendarGrid";
import { CalendarEvent } from "./CalendarEvent";
import { CalendarEventItemType } from "./CalendarEventItem";
import { MonthDayCell } from "./MonthDayCell";

export class MonthCalendarGrid extends CalendarGrid {
    private _daysGrid: HTMLElement;
    protected override _dayCells: Map<string, MonthDayCell> = new Map<string, MonthDayCell>();
    private _scrollTimer: boolean = false;
    private _resizeEvent: any = null;

    public override set currentDate(v: Date) {
        if (v == undefined)
            return;
        if (this._currentDate != undefined && v.getMonth() == this._currentDate.getMonth() && v.getFullYear() == this._currentDate.getFullYear())
            return;

        this._currentDate = v;

        this._redraw();
    }

    public override get currentDate(): Date {
        return this._currentDate;
    }

    protected override _constructor(): void {
        super._constructor();
        this.classList.add("month");
    }

    protected override _showData(eventData: IDataSourceLoadInfo): void {
        super._showData(eventData);
        this._resizeInnerGrid();
    }

    public override selectedCellTimeSpan(): { start: Date; end: Date } {
        const start = this.selectedCell.idOfCell;
        start.setHours(0);
        const end = new Date(start);
        end.setHours(23, 59, 59, 59);
        return { start: start, end: end };
    }

    private _createInnerGrid(): HTMLElement {
        this._daysGrid = createElement({ classes: "days-grid" });

        if (this.currentDate == undefined)
            return this._daysGrid;

        const startDate = new Date(this.currentDate);
        startDate.setDate(1);
        const offset = this.firstDayOfWeek - startDate.getDay();
        startDate.setDate(startDate.getDate() + (offset > 0 ? offset - 7 : offset));

        for (let x = 0; x < 6; x++) {
            const lineGrid = createElement({ classes: "week-grid" });
            for (let y = 0; y < 7; y++) {
                const dayCell = new MonthDayCell();
                dayCell.idOfCell = new Date(startDate);
                dayCell.isCurrentMonth = this._currentDate.getMonth() == startDate.getMonth();
                startDate.setDate(startDate.getDate() + 1);
                lineGrid.appendChild(dayCell);
                dayCell.calendarChangeRequest.add((_sender, params) => {
                    this.calendarChangeRequest.trigger(this, params);
                });

                dayCell.addEventListener("click", (_e: MouseEvent) => {
                    if (this.selectedCell)
                        this.selectedCell.classList.remove("selected");
                    this._selectedCell = dayCell;
                    this.selectedCell.classList.add("selected");
                });

                this._dayCells.set(dayCell.dayId, dayCell);
                this.addEventListener("interactiveChanged", (e: CustomEvent) => {
                    dayCell.interactive = e.detail.value;
                });
            }
            this._daysGrid.appendChild(lineGrid);
        }

        this.addEventListener("wheel", (e: WheelEvent) => {
            if (!this._scrollTimer) {
                this._scrollTimer = true;
                if (e.deltaY > 0)
                    this.intervalChanged.trigger(this, IntervalDirection.next);
                else if (e.deltaY < 0)
                    this.intervalChanged.trigger(this, IntervalDirection.prev);
                self.setTimeout(() => {
                    this._scrollTimer = false;
                }, 500);
            }
        });

        let resizeChecked: number = undefined;
        if (this._resizeEvent)
            window.removeEventListener("resize", this._resizeEvent);
        window.addEventListener("resize", this._resizeEvent = () => {
            if (resizeChecked)
                clearTimeout(resizeChecked);
            resizeChecked = self.setTimeout(() => this._resizeInnerGrid(), 150);
        });

        return this._daysGrid;
    }

    /**
     * Prepocet polozek na jednotlivych dnech
     */
    private _resizeInnerGrid(): void {
        const lineHeight = this._daysGrid.offsetHeight / 6;
        if (lineHeight > 0) {
            this._dayCells.forEach((dayCell) => {
                dayCell.recalculateContent(lineHeight);
            });
        }
    }

    /**
     * Vytvoreni jednoho radku
     */
    protected override _createEventByDS(data: IRowData, uniqueId: string): CalendarEvent {
        const dataRow: CalendarEvent = CalendarEvent.createEventByIRowData(this, uniqueId, data);

        this.dataRows.push(dataRow);

        return dataRow;
    }

    protected override _attachEvent(cv: CalendarEvent): void {
        let items = cv.getCalendarEventItems();

        for (const item of items) {
            const dayCell = this._dayCells.get(item.eventId);
            if (dayCell == undefined)
                continue;
            dayCell.addEventItem(item);

            dayCell.eventAdded.add((md, cvEvent) => {
                //dayCell.removeEmptyItems();

                if (cv == cvEvent)
                    return;
                if (item.type != CalendarEventItemType.standalone)
                    this._updateEventPosition(cv, md);
            });

            item.addEventListener("mouseenter", (_e: MouseEvent) => {
                this._mouseOverItem = cv;
            });

            item.addEventListener("mouseleave", (_e: MouseEvent) => {
                this._mouseOverItem = null;
            });
        }

        items = cv.getCalendarEventItems();

        for (const item of items) {
            const dayCell = this._dayCells.get(item.eventId);
            if (dayCell && (cv.rowNum != item.rowNum)) {
                dayCell.addEmptyItem(item, "before", cv.rowNum - item.rowNum);
            }
        }
    }

    private _createInnerHeaderGrid(): HTMLElement {
        const grid = createElement({ classes: "header-grid" });

        const curCult = CultureInfo.getCultureInfo();
        const curCultName = curCult.name;

        const startDate = new Date("2017-12-31T11:00:00.000Z"); // nedele
        const options: Intl.DateTimeFormatOptions = { weekday: "short" };

        const lineGrid = createElement({ classes: "week-grid" });
        for (let y = 0; y < 7; y++) {
            const dCell = new Date(startDate);
            dCell.setDate(dCell.getDate() + this.firstDayOfWeek + y);

            const hCell = new MonthDayCell();
            hCell.dayText = dCell.toLocaleString(curCultName, options);

            lineGrid.appendChild(hCell);
        }
        grid.appendChild(lineGrid);
        return grid;
    }

    protected override _renderHeader(): void {
        this._mainListDiv.insertAdjacentElement("afterbegin", this._createInnerHeaderGrid());
    }

    protected override _renderDataContainer(): void {
        this._listDiv = document.createElement("div");

        this._listDiv.classList.add("list-div");
        this._mainListDiv.appendChild(this._listDiv);

        const table = document.createElement("table");

        this._dataTable = document.createElement("tbody");
        this._dataTable.classList.add("table-body");

        table.appendChild(this._dataTable);

        this._listDiv.appendChild(table);

        this._mainListDiv.insertAdjacentElement("beforeend", this._createInnerGrid());
    }

    protected override connectedCallback(): void {
        this._resizeInnerGrid();
    }
}

registerCustomElement("tsc-MonthCalendarGrid", MonthCalendarGrid);
